import React from 'react';
import type { ClientSummary } from '../../domain/clients';
import { useClientSearch } from '../../hooks/useClientSearch';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import './CreateRecordWizard.css';

type CreateRecordWizardProps = {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  steps?: readonly string[];
  activeStep?: number;
  progressLabel?: string;
  width?: string;
  className?: string;
  bodyClassName?: string;
  description?: React.ReactNode;
  onClose?: () => void;
  closeDisabled?: boolean;
};

type CreateWizardClientPickerProps = {
  selectedClient: ClientSummary | null;
  onChange: (client: ClientSummary | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
};

type CreateWizardLocationOption = {
  id: string;
  label: string;
};

type CreateWizardLocationSelectProps = {
  value: string | null;
  onChange: (locationId: string | null) => void;
  options: readonly CreateWizardLocationOption[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
};

export function CreateRecordWizard({
  title,
  children,
  footer,
  steps = [],
  activeStep = 0,
  progressLabel = 'Creation progress',
  width = 'min(1040px, calc(100vw - 2rem))',
  className,
  bodyClassName,
  description,
  onClose,
  closeDisabled = false,
}: CreateRecordWizardProps) {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const dialogRef = React.useRef<HTMLDialogElement | null>(null);
  const modal = Boolean(onClose);
  useModalScrollLock(modal);
  React.useLayoutEffect(() => {
    if (!modal) return;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement as HTMLElement | null;
    dialog?.showModal?.();
    return () => { dialog?.close?.(); previousFocus?.focus(); };
  }, [modal]);
  const rootClassName = ['modal', 'wizard', className].filter(Boolean).join(' ');
  const contentClassName = ['wizard-body', bodyClassName].filter(Boolean).join(' ');
  const content = (
    <>
      <div className="wizard-header">
        {onClose ? <button type="button" className="btn-close" aria-label="Cancel" disabled={closeDisabled} onClick={onClose}>×</button> : null}
        <h2 id={titleId} className="m0">{title}</h2>
        {description ? <p id={descriptionId} className="text-sm fg-muted m0">{description}</p> : null}
        {steps.length > 0 ? (
          <ol className="wizard-steps" aria-label={progressLabel}>
            {steps.map((step, index) => {
              const state = index === activeStep ? 'active' : index < activeStep ? 'done' : 'todo';
              return (
                <li
                  key={step}
                  className={`w-step ${state === 'active' ? 'active' : state === 'done' ? 'done' : ''}`}
                  aria-current={state === 'active' ? 'step' : undefined}
                >
                  <span className="w-step__marker" aria-hidden="true">{state === 'done' ? '✓' : index + 1}</span>
                  <span className="w-step__label">{step}</span>
                  <span className="sr-only">
                    {state === 'done' ? ' (completed)' : state === 'active' ? ' (current step)' : ''}
                  </span>
                </li>
              );
            })}
          </ol>
        ) : null}
      </div>
      <div className={contentClassName}>{children}</div>
      {footer ? <div className="wizard-footer">{footer}</div> : null}
    </>
  );
  return onClose ? (
    <dialog ref={dialogRef} className={rootClassName} style={{ width }} aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} onCancel={event => { event.preventDefault(); if (!closeDisabled) onClose(); }}>
      {content}
    </dialog>
  ) : (
    <div className={rootClassName} style={{ width }} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined}>{content}</div>
  );
}

export function CreateWizardBasics({ children, className }: { children: React.ReactNode; className?: string }) {
  const rootClassName = ['grid-basics', className].filter(Boolean).join(' ');
  return <div className={rootClassName}>{children}</div>;
}

export function CreateWizardClientPicker({
  selectedClient,
  onChange,
  label = 'Client',
  required = false,
  disabled = false,
  helperText,
}: CreateWizardClientPickerProps) {
  const clientSearch = useClientSearch();
  const displayLabel = required ? `${label} *` : label;

  return (
    <div className="wizard-client-field">
      <span className="wizard-client-field__label">{displayLabel}</span>
      {selectedClient ? (
        <div className="wizard-client-selected">
          <span className="wizard-client-selected__name">{selectedClient.businessName || 'Unnamed client'}</span>
          <button
            type="button"
            className="btn xs subtle"
            disabled={disabled}
            onClick={() => {
              onChange(null);
              clientSearch.setQuery('');
            }}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <input
            value={clientSearch.query}
            onChange={event => clientSearch.setQuery(event.target.value)}
            placeholder="Search clients by name…"
            aria-label={`Search ${label.toLowerCase()}s`}
            aria-required={required}
            disabled={disabled}
          />
          {clientSearch.error ? <span className="text-xxs fg-danger">{clientSearch.error}</span> : null}
          {clientSearch.query.trim() ? (
            <div className="wizard-client-results" role="listbox" aria-label={`${label} results`}>
              {clientSearch.loading ? (
                <div className="wizard-client-results__empty text-xxs fg-muted">Searching…</div>
              ) : clientSearch.isSuccess && !clientSearch.error && clientSearch.results.length === 0 ? (
                <div className="wizard-client-results__empty text-xxs fg-muted">No clients match that name.</div>
              ) : clientSearch.results.length > 0 ? (
                clientSearch.results.slice(0, 8).map(client => (
                  <button
                    key={client.id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="wizard-client-results__item"
                    onClick={() => {
                      onChange(client);
                      clientSearch.setQuery('');
                    }}
                  >
                    {client.businessName || 'Unnamed client'}
                  </button>
                ))
              ) : null}
            </div>
          ) : null}
          <span className="text-xxs fg-muted">
            {helperText ?? (required ? 'Select a client to continue.' : 'Optional. You can also attach a client later.')}
          </span>
        </>
      )}
    </div>
  );
}

export function CreateWizardLocationSelect({
  value,
  onChange,
  options,
  label = 'Site (Location)',
  placeholder = 'Select a site…',
  required = false,
  disabled = false,
}: CreateWizardLocationSelectProps) {
  return (
    <label>
      {required ? `${label} *` : label}
      <select value={value ?? ''} onChange={event => onChange(event.target.value || null)} disabled={disabled} required={required}>
        <option value="">{placeholder}</option>
        {options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
      </select>
    </label>
  );
}
